import { useEffect, useMemo, useState, MouseEvent } from 'react';
import { first } from 'lodash';
import classNames from 'classnames';
import { sql as sqlLanguage } from '@codemirror/lang-sql';
import { model } from '@gosupersimple/types';

import { useAccountContext } from '@/lib/accounts/context';
import { Button } from '@/components/button';
import { Icon } from '@/components/icon';
import { Loader } from '@/components/loader';
import { CodeMirror, lineNumbers, tabIndentExtension } from '@/components/codemirror';
import { ErrorBoundary, GenericFallback } from '@/lib/error';
import { useIsInView, useLoadingStatus, useInitialQueryLoadCondition } from '@/lib/hooks';
import { useTrackEvent } from '@/lib/analytics';
import { filterVariablesForPipeline } from '@/explore/utils';
import { setFieldIndex, sortProperties } from '@/explore/utils/field-ordering';
import { isSqlCell, setCellTitle, setViewOptions } from '@/core/cell';
import { useMemoizeDataQueryVariables } from '@/explore/utils/use-memoize-data-query-variables';
import { HideInEmbedded } from '@/components/layout/hide-in-embedded';
import { Visualisations } from '@/explore/components/visualisations';
import { DropdownIconSizePx } from '@/components/dropdown';
import { shouldPropagateEvent } from '@/explore/utils/interactable';

import { Model, Pipeline, Sort, SortItem, SqlCell } from '../../types';
import {
  getNodes,
  mapSort,
  useExplorationDataLazyQuery,
  useExplorationDataQuery,
} from '../../../graphql';

import { PaginatedRecords } from '../../components/paginated-records';
import { HorizontalScrollTable } from '../../components/horizontal-scroll-table';
import { MasterBadge } from '../../components/master-badge';
import { DataTableProperty, DataTableRow } from '../../components/datatable';
import { CollapseButton, CollapsibleContainer, CollapsibleContent } from '../collapsible-cell';
import { CellTitle } from '../cell-title';
import { CellControls } from '../cell-controls';
import { PipelinePreview } from '../pipeline-preview';
import { useExplorationContext } from '../exploration-context';
import { useExplorationCellContext } from '../exploration-cell-context';
import { useRefetchCellDataContext } from '../refetch-cell-data-context';
import { useExplorationUIContext } from '../exploration-ui-context';
import { convertRecordTypeTypes } from '../../input';
import { useMetadataContext } from '../../metadata-context';
import { getSqlOperation } from './utils';
import {
  dereferencePipeline,
  getNumberOfChildPipelines,
  getParentPipelineCell,
} from '../../pipeline/utils';
import { useRefetchCellData } from '../hooks';

import container from '../exploration.module.scss';
import style from './sqlcell.module.scss';
import editorStyle from '../code-editor.module.scss';
import table from '../../../components/table/table.module.scss';

interface SqlCellViewProps {
  cell: SqlCell;
  onSelectCell?: () => void;
  onSetDraggable: (value: boolean) => void;
  height?: number;
}

export const SqlCellView = (props: SqlCellViewProps) => {
  const { exploration, createCellInstance } = useExplorationContext();
  const cell = props.cell;
  const { openVisualisationEditor, scrollToCell, selectCell } = useExplorationUIContext();
  const {
    setCell,
    isCollapsible,
    isConversationPart,
    isTableVisible,
    rowContainsConversationPart,
    startConversation,
    addDefaultVisualization,
    isBlockTitleVisible,
    showTable,
    hideTable,
  } = useExplorationCellContext();
  const { refetchAll } = useRefetchCellDataContext();
  const [isDragHovered, setIsDragHovered] = useState(false);
  const trackEvent = useTrackEvent();

  const [containerRef, isInView] = useIsInView();

  const handleCreateCellInstance = () => {
    const { cell: cellInstance } = createCellInstance(cell.id);

    if (cellInstance !== undefined) {
      selectCell(cellInstance.id);
      scrollToCell(cellInstance.id);
    }

    trackEvent('Exploration Cell Instance Created', {
      explorationId: exploration.explorationId,
      explorationName: exploration.name,
      cell,
    });
  };

  const handleSetIsDragHovered = (value: boolean) => {
    props.onSetDraggable(value);
    setIsDragHovered(value);
  };

  const handleAddVisualisation = (event: MouseEvent) => {
    (event.currentTarget as HTMLElement)?.blur();
    const visualisationsLength = props.cell.visualisations?.length ?? 0;
    addDefaultVisualization();
    visualisationsLength > 0 && openVisualisationEditor(visualisationsLength);
  };

  const deleteAllowed = getNumberOfChildPipelines(exploration, cell.pipeline) === 0;

  return (
    <CollapsibleContainer
      className={container.cohortViewCell}
      onClick={(event) => shouldPropagateEvent(event) && props.onSelectCell?.()}
      ref={containerRef}>
      {isBlockTitleVisible && (
        <div className={container.cellHeader}>
          <div className={container.cellControlsContainer}>
            <HideInEmbedded>
              {!isConversationPart && (
                <Icon
                  name="DragHandle"
                  size={10}
                  className={container.dragHandle}
                  onMouseOver={() => handleSetIsDragHovered(true)}
                  onMouseOut={() => handleSetIsDragHovered(false)}
                />
              )}
            </HideInEmbedded>
            <MasterBadge exploration={exploration} pipeline={props.cell.pipeline} />
            <CellTitle
              exploration={exploration}
              value={cell.title ?? '(Untitled)'}
              onChange={(value) => setCell(setCellTitle(cell, value))}
            />
            <CellControls
              exploration={exploration}
              editButtonVisible={false}
              canDelete={deleteAllowed}
              options={(defaultOptions) => [
                ...defaultOptions,
                {
                  label: 'Ask about this block',
                  icon: <Icon name="Zap" size={DropdownIconSizePx} />,
                  onClick: startConversation,
                  sort: 1,
                  disabled: isConversationPart,
                  tooltip: isConversationPart
                    ? 'This block is already part of a conversation'
                    : rowContainsConversationPart
                      ? 'Resolve the current conversation or move the block to a separate row first'
                      : undefined,
                },
                {
                  type: 'divider',
                  sort: 1,
                },
                {
                  key: 'duplicate_as_instance',
                  label: 'Duplicate as instance',
                  icon: <Icon name="Instance" size={DropdownIconSizePx} />,
                  onClick: handleCreateCellInstance,
                  sort: 15,
                },
                {
                  type: 'divider',
                  sort: 24,
                },
                {
                  label: 'Reload data',
                  icon: <Icon name="RefreshCw" size={DropdownIconSizePx} />,
                  onClick: refetchAll,
                  sort: 25,
                },
                {
                  type: 'divider',
                  sort: 50,
                },
                {
                  label: isTableVisible ? 'Hide table' : 'Show table',
                  icon: <Icon name={isTableVisible ? 'EyeOff' : 'Eye'} size={DropdownIconSizePx} />,
                  onClick: () => (isTableVisible ? hideTable() : showTable()),
                  sort: 51,
                  disabled: (cell.visualisations?.length ?? 0) === 0,
                },
              ]}>
              <HideInEmbedded>
                <Button
                  icon={<Icon name="Graphic" />}
                  size="compact"
                  variant="gray"
                  title="Add chart"
                  onClick={handleAddVisualisation}>
                  Add chart
                </Button>
              </HideInEmbedded>
            </CellControls>
            {isCollapsible && <CollapseButton />}
          </div>
        </div>
      )}
      <CollapsibleContent isDragHovered={isDragHovered}>
        <ErrorBoundary fallback={(errorData) => <GenericFallback {...errorData} />}>
          <SqlView cell={cell} setCell={setCell} isInView={isInView} height={props.height} />
        </ErrorBoundary>
      </CollapsibleContent>
    </CollapsibleContainer>
  );
};

interface SqlViewProps {
  cell: SqlCell;
  setCell: (cell: SqlCell) => void;
  isInView: boolean;
  height?: number;
}

const SqlView = (props: SqlViewProps) => {
  const trackEvent = useTrackEvent();

  const sqlOperation = getSqlOperation(props.cell.pipeline);
  const sql = sqlOperation?.parameters.sql ?? '';
  const visualisations = props.cell.visualisations ?? [];
  const isResized = props.height !== undefined;

  const { account, isFeatureEnabled } = useAccountContext();
  const { models } = useMetadataContext();
  const { exploration, getVariables } = useExplorationContext();
  const { openEditor, openVisualisationEditor, scrollToCell, openEditForm } =
    useExplorationUIContext();
  const {
    cell,
    isCollapsed,
    isTableVisible,
    getCellViewOption,
    isPipelinePreviewVisible,
    isBlockTitleVisible,
  } = useExplorationCellContext();
  const [onCompleted, onError, skipInitialLoad] = useInitialQueryLoadCondition(props.isInView);

  if (!isSqlCell(cell)) {
    throw new Error('Not a SQL cell');
  }

  const baseModelId = first(models)?.modelId ?? '';

  const [sort, setSort] = useState<SortItem | null>(null);
  const [recordType, setRecordType] = useState<DataTableProperty[]>([]);

  const pipeline = useMemo(
    () => dereferencePipeline(props.cell.pipeline, exploration),
    [props.cell.pipeline, exploration],
  );
  const variables = filterVariablesForPipeline(pipeline, getVariables());

  const { data, loading, error, fetchMore, refetch } = useExplorationDataQuery({
    variables: useMemoizeDataQueryVariables({
      accountId: account.accountId,
      baseModelId: pipeline.baseModelId,
      pipeline: pipeline.operations,
      sort: mapSort(sort === null ? [] : [sort]),
      variables,
      options: {
        labels: { exploration: exploration.explorationId, block: cell.id },
        optimize: isFeatureEnabled('optimizeQuery'),
      },
    }),
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
    skip: skipInitialLoad || isCollapsed || sql === '',
    onCompleted: (data) => {
      setRecordType(sortProperties(convertRecordTypeTypes(data.account?.query?.recordType) ?? []));
      setPreLoading(false);
      onCompleted();

      trackEvent('Exploration Sql Cell Query Complete', {
        explorationId: exploration.explorationId,
        explorationName: exploration.name,
        cellId: props.cell.id,
        sql,
      });
    },
    onError: () => {
      setPreLoading(false);
      onError();

      trackEvent('Exploration Sql Cell Query Error', {
        explorationId: exploration.explorationId,
        name: exploration.name,
        cellId: props.cell.id,
        sql,
        error,
      });
    },
  });
  useRefetchCellData(refetch);

  useEffect(() => {
    if (loading) {
      trackEvent('Exploration Sql Cell Query Loading', {
        explorationId: exploration.explorationId,
        explorationName: exploration.name,
        cellId: props.cell.id,
        sql,
      });
    }
  }, [loading, exploration.explorationId, exploration.name, props.cell.id, sql, trackEvent]);

  const loaded = data?.account?.query !== undefined;
  const records = getNodes(data?.account?.query);
  const groupedKeys = (data?.account?.query?.grouping ?? []).map(({ key }) => key);

  const fieldOrder = getCellViewOption('fieldOrder');
  const properties = useMemo(
    () => sortProperties(recordType, fieldOrder),
    [fieldOrder, recordType],
  );

  const handleFetchMore = async () => {
    if (loading || !(data?.account?.query?.pageInfo.hasNextPage ?? false)) {
      return;
    }

    fetchMore({ variables: { after: data?.account?.query?.pageInfo.endCursor } });

    trackEvent('Exploration Sql Cell Fetch More', {
      explorationId: exploration.explorationId,
      explorationName: exploration.name,
      cellId: props.cell.id,
      sql,
    });
  };

  const handleSortChange = (sort: SortItem | null) => {
    setSort(sort);

    trackEvent('Exploration Sql Cell Sort Change', {
      explorationId: exploration.explorationId,
      explorationName: exploration.name,
      cellId: props.cell.id,
      sql,
      sort,
    });
  };

  const [preLoading, setPreLoading] = useState(false);
  const [lazyExplorationDataQuery] = useExplorationDataLazyQuery();
  // When sql has changed, we need to know the fields of the new query to update the pipeline. Unfortunately
  // it needs extra exploration data query but it only retrieves the first record to get the fields.
  //
  // There is describeQuery GraphQL query that would be more suitable for this but it does not work with SQL
  // queries yet.
  const handleSqlChange = async (sql: string) => {
    const [sqlOperation, ...operations] = props.cell.pipeline.operations;
    if (sqlOperation.operation !== 'sql' || sqlOperation.parameters.sql === sql) {
      return;
    }

    setPreLoading(true);

    trackEvent('Exploration Sql Cell Sql Change', {
      explorationId: exploration.explorationId,
      explorationName: exploration.name,
      cellId: props.cell.id,
      sql,
    });

    const { data, error } = await lazyExplorationDataQuery({
      variables: {
        accountId: account.accountId,
        baseModelId,
        pipeline: [{ operation: 'sql', parameters: { sql } }],
        first: 1,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    });

    const fields =
      error === undefined
        ? (convertRecordTypeTypes(data?.account?.query?.recordType)
            ?.map(({ key, type }) => ({ key, type }))
            .filter(
              (item): item is { key: string; type: model.PropertyType } => item.type !== null,
            ) ?? [])
        : sqlOperation.parameters.fields;

    props.setCell({
      ...props.cell,
      pipeline: {
        ...props.cell.pipeline,
        baseModelId,
        operations: [{ operation: 'sql', parameters: { sql, fields } }, ...operations],
      },
    });
  };

  const handleEditVisualisation = (idx: number) => {
    openEditor(cell.id);
    openVisualisationEditor(idx);
  };

  return (
    <>
      {isPipelinePreviewVisible && (
        <div
          className={classNames(container.cellHeaderAccessory, {
            [container.noTitleAbove]: !isBlockTitleVisible,
          })}>
          <PipelinePreview
            title={'SQL'}
            pipeline={props.cell.pipeline}
            exploration={exploration}
            variables={variables}
            models={models}
            onClickParent={() =>
              scrollToCell(getParentPipelineCell(exploration, props.cell.pipeline)?.id ?? null)
            }
            onClickOperation={(index) => {
              openEditor(cell.id);
              openEditForm({ index });
            }}
          />
        </div>
      )}

      {visualisations.length > 0 && (
        <div className={container.cellSection}>
          <Visualisations
            visualisations={visualisations}
            onEditVisualisation={handleEditVisualisation}
            isInView={props.isInView}
            isResized={isResized}
          />
        </div>
      )}

      <div className={classNames(container.cellSection, style.inputSection)}>
        <SQLInputView
          sql={sql}
          setSql={handleSqlChange}
          sqlLoaded={loaded}
          error={error?.message}
          loading={loading || preLoading}
          refetch={refetch}
        />
      </div>

      {isTableVisible && (
        <div
          className={classNames(container.cellSection, container.tableSection, {
            [table.groupedTable]: groupedKeys.length > 0,
          })}>
          <SQLResponseView
            records={records}
            cell={cell}
            pipeline={props.cell.pipeline}
            fetchMore={handleFetchMore}
            properties={properties}
            sort={sort === null ? [] : [sort]}
            fieldOrder={fieldOrder ?? {}}
            grouping={groupedKeys}
            setCell={props.setCell}
            setSort={handleSortChange}
            loading={loading || preLoading}
            loaded={loaded && !preLoading}
          />
        </div>
      )}
    </>
  );
};

interface SQLInputViewProps {
  sql: string;
  setSql: (sql: string) => void;
  sqlLoaded: boolean;
  error?: string;
  loading?: boolean;
  refetch: () => void;
}

const SQLInputView = (props: SQLInputViewProps) => {
  const [sql, setSql] = useState(props.sql);
  const [sqlError, setSqlError] = useState<string | undefined>();
  const { isSelectedCell } = useExplorationCellContext();
  // Autofocus only on new empty blocks
  const autofocus = isSelectedCell && sql === '';

  useEffect(() => setSqlError(props.error), [props.error]);
  useEffect(() => setSql(props.sql), [props.sql]);

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    setSqlError(undefined);

    if (sql === '') {
      return;
    }

    const sqlForValidation = sql
      .split('\n')
      .filter((line) => !line.trim().startsWith('--'))
      .join('\n')
      .trim()
      .toLowerCase();

    if (!sqlForValidation.startsWith('select') && !sqlForValidation.startsWith('with')) {
      setSqlError('Query must start with "SELECT" or "WITH"');
      return;
    }

    if (sql !== props.sql) {
      // SQL changed - needs schema discovery too
      props.setSql(sql);
    } else {
      props.refetch();
    }
  };

  return (
    <div className={editorStyle.inputContainer}>
      <CodeMirror
        autoFocus={autofocus}
        value={sql}
        onChange={setSql.bind(this)} // Not sure why this is needed since React 19
        onKeyDown={onKeyDown.bind(this)} // Not sure why this is needed since React 19
        extensions={[sqlLanguage(), lineNumbers(), tabIndentExtension]}
        className={classNames(editorStyle.codeInput, {
          [editorStyle.hasError]: sqlError !== undefined,
        })}
      />

      <div className={editorStyle.buttons}>
        <Button
          variant="primary"
          size="small"
          onClick={handleSubmit}
          disabled={props.loading}
          loading={props.loading}>
          Execute
        </Button>
      </div>

      {sqlError !== undefined && (
        <div className={style.errorContainer}>
          <Icon name="Alert" /> {sqlError}
        </div>
      )}
    </div>
  );
};

const createModelFromProperties = (properties: DataTableProperty[]): Model => ({
  modelId: 'sql-results',
  name: 'SQL',
  primaryKey: [],
  properties: properties.map((property) => ({
    ...property,
    type: property.type ?? 'String',
  })),
  relations: [],
  labels: {},
});

interface SQLResponseViewProps {
  records: DataTableRow[];
  cell: SqlCell;
  setCell: (cell: SqlCell) => void;
  pipeline: Pipeline;
  fetchMore: () => Promise<void>;
  properties: DataTableProperty[];
  sort: Sort;
  fieldOrder: Record<string, number>;
  grouping: string[];
  setSort: (sort: SortItem | null) => void;
  loaded: boolean;
  loading: boolean;
}

const SQLResponseView = (props: SQLResponseViewProps) => {
  const { cell } = useExplorationCellContext();
  const { isFirstLoad, isSubsequentLoad } = useLoadingStatus(props.loading, props.loaded);

  const handleReorderField = (fromIndex: number, toIndex: number, after: boolean) => {
    const fieldKey = props.properties.at(fromIndex)?.key;
    if (fieldKey === undefined) {
      throw new Error('Field key not found for reordering');
    }
    props.setCell(
      setViewOptions(props.cell, {
        ...props.cell.viewOptions,
        fieldOrder: setFieldIndex(fieldKey, fromIndex, toIndex, after, props.fieldOrder),
      }),
    );
  };

  if (!props.loaded && !props.loading) {
    return (
      <div className={editorStyle.empty}>
        <div className={editorStyle.text}>Execute code to see results</div>
      </div>
    );
  }

  return (
    <PaginatedRecords
      properties={props.properties}
      pipeline={props.pipeline}
      sort={props.sort}
      grouping={props.grouping}
      reorderField={handleReorderField}
      setSort={props.setSort}
      records={props.records}
      model={createModelFromProperties(props.properties)}
      fetchMore={props.fetchMore}
      cellHeight={cell.viewOptions?.height}
      footerContent={isSubsequentLoad && <Loader />}
      loading={isFirstLoad}
      component={HorizontalScrollTable}
    />
  );
};
